import { useMemo } from 'react';

import { CHAIN_IDS, HYPERCORE_CHAIN_ID } from '@/constants/chain';

import { ClassifierToken, tokenTier } from '@/utils/slippage/classifier';
import {
  CROSS_CHAIN_MIN_AUTO_SLIPPAGE_BPS,
  getLegsAutoSlippageBps,
} from '@/utils/slippage/getAutoSlippage';
import type { AutoSlippageLeg } from '@/utils/slippage/getAutoSlippage';

import { normalizeTokenAddress } from '../utils/normalizeAddress';
import { useLiquidityScore } from './useLiquidityScore';
import { useTokenAge } from './useTokenAge';

interface UseAutoSlippageBpsParams {
  chainId: number;
  sellToken: ClassifierToken | undefined | null;
  buyToken: ClassifierToken | undefined | null;
  /** Gates the liquidity-score fetch so non-Auto trades incur no extra request. */
  enabled?: boolean;
}

interface UseCrossChainAutoSlippageBpsParams {
  sellToken: ClassifierToken | undefined | null;
  sellChainId: number;
  buyToken: ClassifierToken | undefined | null;
  buyChainId: number;
  enabled?: boolean;
}

export interface AutoSlippage {
  /** Pair category bps (score-enriched when available, resident-signal fallback otherwise). */
  bps: number;
  /**
   * False while the pair's liquidity scores are still loading. Callers gate the quote
   * on this so the request is sent once with the resolved value, never re-quoted when
   * the score settles. Settled = both legs scored, the fetch finished, or it errored
   * (→ resident fallback). Always true for manual / Solana / disabled.
   */
  isReady: boolean;
}

const matchesToken = (
  entry: { address: string; chainId: number },
  key: string,
  chainId: number,
): boolean => entry.chainId === chainId && normalizeTokenAddress(entry.address, chainId) === key;

type TokenRequest = { address: string; chainId: number };
type ScoreData = {
  data: Array<{ address: string; chainId: number; score?: number | null }>;
};
type AgeData = {
  data: Array<{ address: string; chainId: number; poolCreatedAt?: string | null }>;
};
type AutoSlippagePair = readonly [AutoSlippageLeg, AutoSlippageLeg];

const attachAttributes = (
  token: ClassifierToken | undefined | null,
  chainId: number,
  scoreData: ScoreData | undefined,
  ageData: AgeData | undefined,
): ClassifierToken | undefined | null => {
  if (!token?.address) return token;

  const key = normalizeTokenAddress(token.address, chainId);
  const scoreItem = scoreData?.data.find((entry) => matchesToken(entry, key, chainId));
  const ageItem = ageData?.data.find((entry) => matchesToken(entry, key, chainId));
  const createdAtMs = ageItem?.poolCreatedAt ? Date.parse(ageItem.poolCreatedAt) : Number.NaN;

  return {
    ...token,
    liquidityScore: typeof scoreItem?.score === 'number' ? scoreItem.score : undefined,
    ageSeconds: Number.isFinite(createdAtMs)
      ? Math.max(0, (Date.now() - createdAtMs) / 1000)
      : undefined,
  };
};

const settled = (
  requested: TokenRequest[] | undefined,
  response: { data: Array<{ address: string; chainId: number }> } | undefined,
  fetching: boolean,
  fetchError: unknown,
): boolean => {
  const hasAllEntries =
    !!requested?.length &&
    requested.every((token) => {
      const key = normalizeTokenAddress(token.address, token.chainId);
      return response?.data.some((entry) => matchesToken(entry, key, token.chainId));
    });

  return !requested?.length || hasAllEntries || (!fetching && !!response) || !!fetchError;
};

const usePairAutoSlippageBps = (
  legs: AutoSlippagePair,
  { floorBps, enabled }: { floorBps: number; enabled: boolean },
): AutoSlippage => {
  const [sellLeg, buyLeg] = legs;
  const { token: sellToken, chainId: sellChainId } = sellLeg;
  const { token: buyToken, chainId: buyChainId } = buyLeg;

  const scoreTokens = useMemo(() => {
    if (!enabled) return undefined;

    const tokens = [
      { token: sellToken, chainId: sellChainId },
      { token: buyToken, chainId: buyChainId },
    ]
      .filter(
        ({ token, chainId }) =>
          !!token?.address && chainId !== CHAIN_IDS.SOLANA && chainId !== HYPERCORE_CHAIN_ID,
      )
      .map(({ token, chainId }) => ({ address: token!.address as string, chainId }));

    return tokens.length > 0 ? tokens : undefined;
  }, [buyChainId, buyToken, enabled, sellChainId, sellToken]);

  // Only a longtail leg can escalate to an age tier, so resident stable/major legs
  // never need an age.
  const ageTokens = useMemo(() => {
    const tokens = scoreTokens?.filter(({ address, chainId }) => {
      const tier = tokenTier({ address } as ClassifierToken, chainId);
      return tier !== 'stable' && tier !== 'major';
    });
    return tokens?.length ? tokens : undefined;
  }, [scoreTokens]);

  const { data, error, isFetching } = useLiquidityScore(scoreTokens);
  const { data: ageData, error: ageError, isFetching: isAgeFetching } = useTokenAge(ageTokens);

  return useMemo(() => {
    const enrichedLegs = [
      {
        token: attachAttributes(sellToken, sellChainId, data, ageData),
        chainId: sellChainId,
      },
      {
        token: attachAttributes(buyToken, buyChainId, data, ageData),
        chainId: buyChainId,
      },
    ];
    const bps = getLegsAutoSlippageBps(enrichedLegs, floorBps);
    const isReady =
      settled(scoreTokens, data, isFetching, error) &&
      settled(ageTokens, ageData, isAgeFetching, ageError);

    return { bps, isReady };
  }, [
    ageData,
    ageError,
    ageTokens,
    buyChainId,
    buyToken,
    data,
    error,
    floorBps,
    isAgeFetching,
    isFetching,
    scoreTokens,
    sellChainId,
    sellToken,
  ]);
};

/**
 * Auto slippage (bps) for an EVM pair, enriching the classifier with the pair's
 * liquidity scores and (for possible-longtail legs) the token's earliest-pool age,
 * which escalates a young longtail to an age tier (`nascent`/`emerging`/`volatile`).
 * The fetches are gated on `enabled` and `isReady` stays false until they settle, so
 * callers can hold the quote back and avoid a slippage flip. While attributes load
 * (or for Solana / when disabled) `bps` falls back to the resident signals; an unknown
 * age never escalates.
 * A shared query + logic across call sites keeps displayed and submitted values in sync.
 */
export const useAutoSlippageBps = ({
  chainId,
  sellToken,
  buyToken,
  enabled = true,
}: UseAutoSlippageBpsParams): AutoSlippage =>
  usePairAutoSlippageBps(
    [
      { token: sellToken, chainId },
      { token: buyToken, chainId },
    ],
    { floorBps: 0, enabled },
  );

/** Resolves cross-chain Auto from both legs while skipping remote signals for Solana and HyperCore. */
export const useCrossChainAutoSlippageBps = ({
  sellToken,
  sellChainId,
  buyToken,
  buyChainId,
  enabled = true,
}: UseCrossChainAutoSlippageBpsParams): AutoSlippage =>
  usePairAutoSlippageBps(
    [
      { token: sellToken, chainId: sellChainId },
      { token: buyToken, chainId: buyChainId },
    ],
    { floorBps: CROSS_CHAIN_MIN_AUTO_SLIPPAGE_BPS, enabled },
  );
